import React from "react";
import { ApiError } from "@atlas/shared";
import { useMutation } from "@tanstack/react-query";
import type { FeedbackReply, ReplyEmailStatus } from "../api/feedback";
import { Button } from "./Button";

// Shared rendering of a feedback thread for the admin inbox and the user's own
// feedback page. The original submission is passed in as the first message;
// `viewerRole` decides which side reads as "You". Composer is included so both
// surfaces post the same way; the caller supplies the mutation function.

export interface ThreadMessage {
  id: string;
  authorRole: "admin" | "user";
  authorName: string | null;
  body: string;
  createdAt: string;
  /** Admin-only delivery chip, shown to admins. */
  emailStatus?: ReplyEmailStatus | null;
}

const EMAIL_STATUS_LABEL: Record<ReplyEmailStatus, string> = {
  queued: "Email queued",
  skipped_placeholder: "In-app only: no real email",
  failed_to_queue: "Email not queued",
};

function formatDate(value: string) {
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function submissionAsMessage(f: {
  id: string;
  body: string;
  createdAt: string;
  submitterName?: string | null;
}): ThreadMessage {
  return {
    id: `submission-${f.id}`,
    authorRole: "user",
    authorName: f.submitterName ?? null,
    body: f.body,
    createdAt: f.createdAt,
  };
}

export function FeedbackThread({
  messages,
  viewerRole,
  showDelivery = false,
}: {
  messages: ThreadMessage[];
  viewerRole: "admin" | "user";
  showDelivery?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {messages.map((m) => {
        const mine = m.authorRole === viewerRole;
        const who = mine ? "You" : m.authorName || (m.authorRole === "admin" ? "Atlas" : "Submitter");
        return (
          <div
            key={m.id}
            style={{
              alignSelf: mine ? "flex-end" : "flex-start",
              maxWidth: "min(560px, 92%)",
              minWidth: 0,
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              background: mine ? "var(--surface-raised)" : "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                flexWrap: "wrap",
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>{who}</span>
              <span>{formatDate(m.createdAt)}</span>
              {showDelivery && m.emailStatus && (
                <span
                  style={{
                    color: m.emailStatus === "queued" ? "var(--text-muted)" : "var(--status-danger)",
                  }}
                >
                  {EMAIL_STATUS_LABEL[m.emailStatus]}
                </span>
              )}
            </div>
            <p
              style={{
                fontSize: 13.5,
                color: "var(--text-primary)",
                margin: 0,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {m.body}
            </p>
          </div>
        );
      })}
    </div>
  );
}

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 76,
  padding: "10px 12px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: "var(--text-base)",
  fontFamily: "var(--font-sans)",
  lineHeight: 1.5,
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
};

export const REPLY_MAX_LENGTH = 4000;

export function ReplyComposer({
  send,
  onSent,
  disabledReason,
  warning,
  placeholder = "Write a reply",
  buttonLabel = "Send reply",
}: {
  send: (body: string) => Promise<{ reply: FeedbackReply }>;
  onSent: () => void | Promise<unknown>;
  /** When set, the composer renders the reason instead of a usable box. */
  disabledReason?: string;
  /** Shown above the box (e.g. "no real email, in-app only"). */
  warning?: string;
  placeholder?: string;
  buttonLabel?: string;
}) {
  const [body, setBody] = React.useState("");
  const mutation = useMutation({
    mutationFn: (text: string) => send(text),
    onSuccess: async () => {
      setBody("");
      await onSent();
    },
  });

  if (disabledReason) {
    return (
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "10px 0 0" }}>{disabledReason}</p>
    );
  }

  const error = mutation.error;
  const errorText =
    error instanceof ApiError
      ? (typeof error.body?.["message"] === "string" ? (error.body["message"] as string) : error.message)
      : error
        ? "Couldn't send that. Try again."
        : null;
  const trimmed = body.trim();

  return (
    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      {warning && (
        <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: 0 }}>{warning}</p>
      )}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        maxLength={REPLY_MAX_LENGTH}
        style={textareaStyle}
      />
      <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={mutation.isPending || trimmed.length === 0}
          onClick={() => mutation.mutate(trimmed)}
        >
          {mutation.isPending ? "Sending…" : buttonLabel}
        </Button>
        {errorText && <span style={{ fontSize: 12.5, color: "var(--status-danger)" }}>{errorText}</span>}
      </div>
    </div>
  );
}
